/* istanbul ignore file - top-level error handler, not unit tested */

import React, { Component, ErrorInfo, ReactNode } from "react";

import {
  Button,
  Flex,
  Spacer,
  Text,
  Heading,
  Illustration,
} from "@kazoohr/confetti";

import styles from "./errorBoundary.scss";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          className={styles.container}
        >
          <div className={styles.illustration}>
            <Illustration illustration="okrs" />
          </div>
          <Spacer size="large" />
          <Heading level="1">Something went wrong</Heading>
          <Spacer size="tiny" />
          <Text paragraph className={styles.message}>
            We encountered an unexpected error. Please try reloading the page.
          </Text>
          {this.state.error && (
            <>
              <Spacer size="small" />
              <Text size="paragraphSmall" className={styles.errorDetails}>
                {this.state.error.message}
              </Text>
            </>
          )}
          <Spacer size="large" />
          <Button variant="primary" onClick={this.handleReload}>
            Reload Page
          </Button>
        </Flex>
      );
    }

    return this.props.children;
  }
}
